import React, { useState, useEffect, useRef } from 'react';
import { useDataProvider, Button as AdminButton, Title, UPDATE, useTranslate } from 'react-admin';
import SelectableRowsTable from '../Tables/SelectableRowsTable';
import { Card, Box } from '@mui/material';
import { useSnackbar } from 'notistack';
import { ASSOCIAZIONE_MASSIVA_STATUS_TYPES } from '../objects';
import { checkDeviceCountPerTaskNMUs, getDevicesById, getDevicesByNMUInAttribuito, getTaskById } from '../../Utilities/helperFunctions';
import { validateDevices } from '../../Utilities/validateDevices';
import AssociazioneMassivaHeader from './AssociazioneMassivaHeader';
import { GET_ONE } from 'ra-core';
import SaveIcon from '@mui/icons-material/Save';


export const ThemeContext = React.createContext('associazionemassiva');

/**
 * Opens a dialog to associate several devices to a task
 * 
 * React context is used here to share state with the embedded table elements
 * 
 * Standard props are still used whenever only one component is making use of that and/or there 
 * is no forecast of that value being required by other components in the future
 * @param {object} translate Imported by default with translate. Neccessary to translate strings
 */
function AssociazioneMassiva({}) {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [workRequestId, setWorkRequestId] = useState('');
    const translate = useTranslate();
    const [filters, setFilters] = useState();
    const [devices, setDevices] = useState([]);
    const [task, setTask] = useState();
    const [NMUQuantities, setNMUQuantities] = useState();
    const [taskError, setTaskError] = useState(false);
    const [confirmEnabled, setConfirmEnabled] = useState(false);
    const [executeUpdate, setExecuteUpdate] = useState(false);
    const [rows, setRows] = React.useState([]);
    const [selected, setSelected] = React.useState([]);
    const [isValidating, setIsValidating] = useState(false);
    const [enableSorting, setEnableSorting] = useState(true);
    const [hasChanged, setHasChanged] = useState(false);
    const rowsRef = useRef([]);
    rowsRef.current = rows;

    const dataProvider = useDataProvider();


    useEffect(() => {
        return () => {
            closeSnackbar();
        }
    }, [])

    useEffect(() => {
        const getNomenclatore = async () => {
            if (task) {
                let splitStringComma = task.ListaNMU.split(",");
                let nmuQuantities = [];
                for (let i = 0; i < splitStringComma.length; i++) {
                    const nmu = splitStringComma[i].split(":")[0];
                    if (nmu !== '') {
                        const quantity = splitStringComma[i].split(":")[1];
                        const response = await dataProvider(GET_ONE, 'nomenclatores', { cod_nmu: nmu });
                        if (response.data?.docs?.length > 0) {
                            const nomenclatore = response.data?.docs[0];
                            nmuQuantities = [
                                ...nmuQuantities,
                                {
                                    values: [...nomenclatore.comp_nmu, nmu],
                                    quantity: quantity,
                                    selected: 0
                                }
                            ]
                        }
                    }
                }
                setNMUQuantities(nmuQuantities);
            }
        }

        getNomenclatore();

    }, [task])

    useEffect(() => {
        if (isValidating) getDevicesAndValidate();
    }, [isValidating])

    useEffect(() => {
        if (selected?.length > 0) {
            checkRowsStatuses();
            setHasChanged(true);
        }
    }, [selected])

    useEffect(() => {
        if (devices && devices.length > 0) {
            let newRows = [];
            for (let device of devices) {
                newRows.push({ device, status: undefined, loading: false })
            }
            setRows(newRows);
        }
        else {
            setRows([]);
            setSelected([]);
        }
    }, [task, devices])

    useEffect(() => {
        if (NMUQuantities) {
            const fetchDevices = async () => {
                let NMUs = NMUQuantities.map(nmu => nmu.values).reduce((prev, current) => [...prev, ...current]);
                const devices = await getDevicesByNMUInAttribuito(NMUs, true);
                setDevices(devices);
            }
            fetchDevices();
        }
    }, [NMUQuantities])

    useEffect(() => {
        return () => {
            setWorkRequestId('');
            setConfirmEnabled(false);
            setTaskError(false);
            setNMUQuantities();
            setRows();
            setTask();
            setDevices();
            setSelected();
            setHasChanged(false);
        }
    }, [])

    useEffect(() => {
        if (executeUpdate) {
            setConfirmEnabled(false);
            let devicesSendToSap = [];
            let devicesUpdate = [];
            let promisesArray = [];

            for (let i = 0; i < rows.length; i++) {
                const deviceIndex = selected.indexOf(rows[i].device.id);
                rows[i].device.status = rows[i].device.newStatus;
                if (deviceIndex !== -1) {
                    if (rows[i].device.sendToSap) {
                        delete rows[i].device.id.sendToSap;
                        devicesSendToSap.push(rows[i].device.id);
                    }

                    devicesUpdate.push(rows[i].device)
                }
            }

            for (let i = 0; i < devicesUpdate.length; i++) {
                if (devicesUpdate[i].reverse?.codice === '1') {
                    devicesUpdate[i].status = { codice: '0', name: 'Da Esaminare' };
                    devicesUpdate[i].anomalia = { codice: '8', name: 'GUASTO AL PRIMO UTILIZZO' };

                    let positionToRemove = devicesSendToSap.indexOf(devicesUpdate[i].id);
                    if (positionToRemove > -1) {
                        devicesSendToSap.splice(positionToRemove, 1);
                    }
                }
                let updateProps = {
                    id: devicesUpdate[i].id,
                    data: devicesUpdate[i],
                };
                promisesArray.push(dataProvider(UPDATE, 'devices', updateProps))
            }

            Promise.all(promisesArray).then(() => {
                if (devicesSendToSap.length > 0) {
                    let user = localStorage.getItem('username');
                    new Promise(resolve => {
                        dataProvider(UPDATE, 'sendtosap', {
                            "username": user,
                            "body": devicesSendToSap
                        }).then(result => resolve(result))
                    })
                        .then(() => {
                            showMessage("AGGIORNAMENTO RIUSCITO ", 'success');
                            resetState();
                        })
                }
            })
                .then(() => {
                    showMessage("AGGIORNAMENTO RIUSCITO ", 'success');
                    resetState();
                })
        }
    }, [executeUpdate])

    useEffect(() => {
        resetState(workRequestId);
    }, [workRequestId])

    const resetState = (workrequestid) => {
        closeSnackbar();
        setDevices();
        setWorkRequestId(workrequestid || '');
        setTaskError();
        setTask();
        setFilters();
        setRows();
        setNMUQuantities();
        setSelected();
    }

    const resetStateAndGetTask = (workrequestid) => {
        setDevices();
        setTaskError();
        setTask();
        setFilters();
        setRows();
        setNMUQuantities();
        getTask(workrequestid);
    }

    const updateRow = (device, type, message) => {
        let newRow = { device: {}, status: {} };
        if (type) newRow.status.type = type;
        if (message) newRow.status.message = message;
        else if (type === ASSOCIAZIONE_MASSIVA_STATUS_TYPES.SUCCESS) newRow.status.message = 'Successo'
        newRow.device = device;
        newRow.loading = false;
        let tempRows = rows;
        let index = tempRows.findIndex(r => r.device.id === newRow.device.id)
        tempRows.splice(index, 1, newRow);
        setRows([...tempRows]);
    }

    const checkRowsStatuses = () => {
        if (task && rows.length > 0 && selected.length > 0) {
            let validRowsCounter = 0;
            let selectedDevices = [];

            for (let id of selected) {
                const row = rows.find(el => el.device.id === id);
                selectedDevices.push(row.device);
                if (row?.status?.type === ASSOCIAZIONE_MASSIVA_STATUS_TYPES["SUCCESS"]) validRowsCounter++;
            }

            let isCountValid = true;
            if (task.ProprietaMateriale === 'O') isCountValid = checkDeviceCountPerTaskNMUs(NMUQuantities);

            if ((validRowsCounter === selected.length) && isCountValid) setConfirmEnabled(true);
            else setConfirmEnabled(false);
        }
    }

    const handleSelectRow = (e, row, settingReverse, unsettingReverse) => {
        closeSnackbar();
        const selectedIndex = selected.indexOf(row.device.id);
        let newSelected = selected;
        let affectedNMUQuantitiesObject = NMUQuantities.find(nmu => nmu.values.some(val => val == row.device.cod_nmu));
        let newRow = JSON.parse(JSON.stringify(row));
        let reverse = (row.device.reverse && row.device.reverse.codice === '1');

        if (selectedIndex === -1) {
            newSelected.push(row.device.id);
            if (!reverse) affectedNMUQuantitiesObject.selected++;
        }
        else {
            if (settingReverse) {
                affectedNMUQuantitiesObject.selected--;
            }
            else if (unsettingReverse) {
                if (affectedNMUQuantitiesObject.selected >= affectedNMUQuantitiesObject.quantities) {
                    newSelected.splice(selectedIndex, 1);
                }
                else affectedNMUQuantitiesObject.selected++;
            }
            else {
                if (!reverse) {
                    affectedNMUQuantitiesObject.selected--;
                }
                newSelected.splice(selectedIndex, 1);
            }
        }

        if (!e.target.checked) {
            delete newRow.device.reverse;
        }
        updateRow(newRow.device);
        setSelected([...newSelected]);
    };

    const getDevicesAndValidate = async () => {
        let devices = await getDevicesById(selected)
        await new Promise(resolve => {
            let tempDevices = devices;

            for (let id of selected) {
                const row = rowsRef.current.find(row => row.device.id === id);
                if (row.device.reverse) {
                    let index = tempDevices.findIndex(device => device.id === row.device.id);
                    tempDevices[index].reverse = { codice: '1', name: 'Guasto al Primo utilizzo' };
                }
            }

            resolve(validateDevices(tempDevices, task, updateRow, translate, workRequestId, NMUQuantities, showMessage));
        });
        setHasChanged(false);
        setIsValidating(false);
        checkRowsStatuses();
    };

    const getTask = () => {
        if (workRequestId) {
            getTaskById(workRequestId)
                .then(task => {
                    if (task.errore) setTaskError(task.errore);
                    else if (!task.ListaNMU) setTaskError(translate('tim.associazioneMassiva.cr173_NoListaNMUInTask'));
                    else setTask(task);
                })
                .catch(error => showMessage(error?.message, 'error'));
        }
    }

    const showMessage = (message, level) => {
        let customMessage = message;
        let stringifiedError = JSON.stringify(message);
        if (stringifiedError.includes('ECONNRESET')) {
            customMessage = 'La ricerca attività ha restituito un errore di timeout'
        }

        enqueueSnackbar(customMessage, {
            variant: level,
            anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'center',
            },
            autoHideDuration: 60000,
        });
    }

    /* Somewhat of an anti pattern to keep this many props. Should further split components into smaller ones to simplify state  */
    return (
        <ThemeContext.Provider value={{
            showMessage,
            workRequestId, setWorkRequestId,
            filters, setFilters,
            devices, setDevices,
            task, setTask,
            taskError, setTaskError,
            NMUQuantities, setNMUQuantities,
            executeUpdate, setExecuteUpdate,
            selected, setSelected,
            setEnableSorting,
            isValidating, setIsValidating,
            setConfirmEnabled,
            rows, setRows, updateRow,
            hasChanged, setHasChanged,
            rowsRef, handleSelectRow,
            resetStateAndGetTask
        }}>
            <Card sx={{ marginTop: '1rem'}}>
                <Title title="Associazione Massiva" />
                <AssociazioneMassivaHeader />
                <SelectableRowsTable />
                <Box sx={{ display: 'flex', margin: '1em 1em 0.5em' }}>
                    <AdminButton
                    variant="contained"
                        label="tim.associazioneMassiva.confirm"
                        onClick={() => setExecuteUpdate(true)}
                        disabled={!confirmEnabled}
                    >
                        <SaveIcon />
                    </AdminButton>
                    {<AdminButton sx={{ marginLeft: 'auto' }} label='tim.associazioneMassiva.cancel' onClick={() => resetState(workRequestId)} color="primary"></AdminButton>}
                </Box>
            </Card>
        </ThemeContext.Provider>
    );
}

export default AssociazioneMassiva;
